import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'
import type { ApiClient, Tier1Report } from '../api/client'
import { readSnapshots } from '../api/stream'
import { plural, t, type Key } from '../i18n/dict'
import { describeError, problem, type Problem } from '../i18n/errors'
import { money, pct, when } from '../i18n/format'
import { decimal, formats } from '../i18n/locale'
import { ruleMessage } from '../i18n/rules'
import { evaluate } from '../rules/interpreter'
import type { CheckingReason, TicketState, Verdict, Violation } from '../rules/types'
import type { GatekeeperHost, Quote, SymbolInfo } from '../sdk/adapter'
import { ServerClock } from '../serverClock'
import type { DecisionSnapshot, OrderIntent, TradeSide } from '../types/snapshot'
import { ProblemLine } from '../ProblemLine'
import { lotsOf, sizeFromRisk, stopFromPips } from './sizing'
import { startTiming, summarise, type TicketTiming } from './timing'

/**
 * The order ticket.
 *
 * Success criterion for this component is not correctness, it is speed: if it is slower
 * than cTrader's native ticket, nobody uses it and the whole enforcement story collapses
 * back to the guardian's reactive guarantees. Hence Tier 1 — the verdict below is computed
 * locally on every keystroke, with no network in the path — and size from risk: the trader
 * types where the stop goes and how much of the account may go; the size follows.
 *
 * Tier 2 confirms in the background (debounced, never gating). Confirm is Tier 3: the API
 * records the decision, and on "cleared" THIS component sends the order through the SDK in
 * the trader's own session, labelled so the guardian attributes the fill.
 */
export interface TicketProps {
  api: ApiClient
  host: GatekeeperHost
  account: number
  /** From the placement's query string, e.g. XAUUSD. */
  initialSymbol: string | null
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'deciding' }
  | { kind: 'sending'; label: string }
  | { kind: 'filled'; label: string }
  /** The violations themselves, not their English: the ticket translates them like any
   * other, and a string here would be the one place it could not. */
  | { kind: 'refused'; violations: readonly Violation[] }
  | { kind: 'failed'; problem: Problem }

export function Ticket({ api, host, account, initialSymbol }: TicketProps) {
  const clock = useMemo(() => new ServerClock(), [])
  const [snapshot, setSnapshot] = useState<DecisionSnapshot | null>(null)
  const [streamStatus, setStreamStatus] = useState<'connecting' | 'connected' | 'reconnecting' | 'denied'>('connecting')
  const [symbols, setSymbols] = useState<{ symbolId: number; name: string }[]>([])
  const [symbol, setSymbol] = useState<SymbolInfo | null>(null)
  const [quote, setQuote] = useState<Quote | null>(null)
  const [side, setSide] = useState<TradeSide>('Buy')
  const [stopMode, setStopMode] = useState<'pips' | 'price'>('pips')
  const [stopText, setStopText] = useState('20')
  const [riskText, setRiskText] = useState('0.5')
  const [takeProfitText, setTakeProfitText] = useState('')
  const [tier2, setTier2] = useState<{ sequence: number; agrees: boolean | null } | null>(null)
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const timing = useRef<TicketTiming>(startTiming())
  const lastSnapshotAt = useRef(0)
  // Ticks arrive several times a second; the verdict re-renders on each one anyway, so a
  // ref-free re-render on every quote is the honest cost of a live ticket.

  // ── the snapshot stream ────────────────────────────────────────────────
  useEffect(() => {
    const controller = new AbortController()
    void readSnapshots(
      (signal) => api.openStream(account, signal),
      (next) => {
        clock.sync(next.serverTime)
        lastSnapshotAt.current = Date.now()
        setSnapshot(next)
      },
      setStreamStatus,
      controller.signal,
    )
    return () => controller.abort()
  }, [api, account, clock])

  // ── a fallback for anything between us and the API that buffers ────────
  // Seen in cTrader itself: through an intermediary that buffers the response, the SSE stream
  // opens, delivers nothing, and drops — over and over — while the API is perfectly healthy,
  // and the ticket sits at "waiting for the guardian". The condition is deliberately "no
  // snapshot arrived recently" rather than the stream's status, because that status flaps
  // between connected and reconnecting the whole time this is happening. A snapshot carries
  // its own staleAfter, so a polled one is exactly as safe as a streamed one.
  useEffect(() => {
    let cancelled = false
    const pull = () => {
      // Elapsed local time, never a rule decision: ServerClock has nothing to say until
      // the first snapshot has arrived, which is precisely the case this exists for.
      if (Date.now() - lastSnapshotAt.current < 10_000) return
      void api
        .snapshot(account)
        .then((next) => {
          if (cancelled) return
          clock.sync(next.serverTime)
          lastSnapshotAt.current = Date.now()
          setSnapshot(next)
        })
        .catch(() => undefined)
    }

    pull()
    const timer = setInterval(pull, 5_000)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [api, account, clock])

  // ── the host: symbols, then the one in front of the trader ─────────────
  useEffect(() => {
    let cancelled = false
    void host
      .listSymbols()
      .then((list) => {
        if (cancelled) return
        setSymbols(list)
        const wanted = initialSymbol?.toUpperCase()
        const pick = list.find((s) => s.name.toUpperCase() === wanted) ?? list.find((s) => s.name === 'EURUSD') ?? list[0]
        if (pick) void host.getSymbol(pick.symbolId).then((info) => !cancelled && setSymbol(info))
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [host, initialSymbol])

  useEffect(() => {
    if (!symbol) return
    setQuote(null)
    timing.current = startTiming()
    const subscription = host.subscribeQuotes([symbol.symbolId]).subscribe({ next: setQuote, error: () => undefined })
    return () => subscription.unsubscribe()
  }, [host, symbol])

  // ── the intent, derived from what the trader typed ─────────────────────
  const price = quote ? (side === 'Buy' ? quote.ask : quote.bid) : null
  const stopLoss = useMemo(() => {
    if (!symbol || price === null) return null
    const value = decimal(stopText)
    if (!(value > 0)) return null
    return stopMode === 'pips' ? stopFromPips(price, value, side, symbol.digits) : value
  }, [symbol, price, stopText, stopMode, side])

  const sizing = useMemo(() => {
    if (!symbol || price === null || stopLoss === null || !snapshot) return null
    return sizeFromRisk({
      equity: snapshot.equity,
      riskPercent: decimal(riskText) || 0,
      price,
      stopLoss,
      minVolume: symbol.minVolume,
      maxVolume: symbol.maxVolume,
      stepVolume: symbol.stepVolume,
    })
  }, [symbol, price, stopLoss, snapshot, riskText])

  const takeProfit = decimal(takeProfitText) > 0 ? decimal(takeProfitText) : undefined

  const intent = useMemo<OrderIntent | null>(() => {
    if (!symbol || price === null || !sizing || sizing.volume <= 0) return null
    return {
      account,
      symbol: symbol.symbolId,
      side,
      type: 'Market',
      volume: sizing.volume,
      price,
      ...(stopLoss === null ? {} : { stopLoss }),
      ...(takeProfit === undefined ? {} : { takeProfit }),
      clientMsgId: '', // assigned at confirm; Tier 1 does not need one
    }
  }, [symbol, price, sizing, account, side, stopLoss, takeProfit])

  // ── Tier 1 ─────────────────────────────────────────────────────────────
  const state = useTicketState(clock, snapshot, intent, account)
  if ((state.status === 'Ready' || state.status === 'Blocked') && timing.current.firstVerdictAt === null) {
    timing.current.firstVerdictAt = performance.now()
  }

  // ── Tier 2, debounced, never gating ────────────────────────────────────
  useEffect(() => {
    if (!intent || !snapshot || (state.status !== 'Ready' && state.status !== 'Blocked')) return
    const report = tier1Of(snapshot, state.verdict)
    const handle = setTimeout(() => {
      api
        .validate({ ...intent, clientMsgId: 'tier2' }, report)
        .then((r) => setTier2({ sequence: r.snapshotSequence, agrees: r.agrees }))
        .catch(() => setTier2(null))
    }, 150)
    return () => clearTimeout(handle)
  }, [api, intent, snapshot, state])

  // ── Tier 3, then the send ──────────────────────────────────────────────
  const confirm = useCallback(async () => {
    if (!intent || !snapshot || !symbol || state.status !== 'Ready') return
    const final: OrderIntent = { ...intent, clientMsgId: uuid() }
    setPhase({ kind: 'deciding' })
    try {
      const decision = await api.submit(final, tier1Of(snapshot, state.verdict))
      if (decision.status !== 'cleared') {
        setPhase({ kind: 'refused', violations: decision.verdict.violations })
        return
      }
      setPhase({ kind: 'sending', label: decision.label })
      timing.current.sentAt = performance.now()
      const execution = await host.placeOrder({
        symbolId: symbol.symbolId,
        side,
        volume: final.volume,
        ...(final.stopLoss === undefined ? {} : { stopLoss: final.stopLoss }),
        ...(final.takeProfit === undefined ? {} : { takeProfit: final.takeProfit }),
        label: decision.label,
        clientOrderId: decision.clientOrderId,
      })
      if (execution?.errorCode) {
        setPhase({ kind: 'failed', problem: problem(t('phase.rejected', { code: execution.errorCode })) })
        api.telemetry('order-rejected', { intent: final.clientMsgId, error: execution.errorCode, ...summarise(timing.current) })
        return
      }
      timing.current.filledAt = performance.now()
      setPhase({ kind: 'filled', label: decision.label })
      api.telemetry('order-filled', { intent: final.clientMsgId, symbol: symbol.name, ...summarise(timing.current) })
    } catch (error) {
      // Tier 3 answers 503 when it will not decide — a stale snapshot, or an account it cannot
      // see. That is a sentence the trader can act on, not a stack trace.
      setPhase({ kind: 'failed', problem: describeError(error, 'error.noSnapshot') })
    }
  }, [api, host, intent, snapshot, symbol, side, state])

  // ── render ─────────────────────────────────────────────────────────────
  const digits = symbol?.digits ?? 5
  const fmt = (n: number | null | undefined) => (n === null || n === undefined ? '—' : n.toFixed(digits))

  return (
    <section className="ticket" aria-label={t('ticket.aria')}>
      <header className="ticket-head">
        <select
          className="symbol"
          value={symbol?.symbolId ?? ''}
          onChange={(e) => void host.getSymbol(Number(e.target.value)).then(setSymbol)}
          aria-label={t('ticket.symbolAria')}
        >
          {symbols.map((s) => (
            <option key={s.symbolId} value={s.symbolId}>
              {s.name}
            </option>
          ))}
        </select>
      </header>

      <div className="sides" role="radiogroup" aria-label={t('ticket.directionAria')}>
        <button type="button" className={side === 'Sell' ? 'sell on' : 'sell'} onClick={() => setSide('Sell')} aria-pressed={side === 'Sell'}>
          {t('ticket.sell')} {fmt(quote?.bid)}
        </button>
        <button type="button" className={side === 'Buy' ? 'buy on' : 'buy'} onClick={() => setSide('Buy')} aria-pressed={side === 'Buy'}>
          {t('ticket.buy')} {fmt(quote?.ask)}
        </button>
      </div>

      <div className="fields">
        <label>
          <span>{t('ticket.stop')}</span>
          <span className="stop-input">
            <input inputMode="decimal" value={stopText} onChange={(e) => setStopText(e.target.value)} aria-label={t('ticket.stopAria')} />
            <button type="button" className="unit" onClick={() => setStopMode(stopMode === 'pips' ? 'price' : 'pips')}>
              {t(stopMode === 'pips' ? 'ticket.unit.pips' : 'ticket.unit.price')}
            </button>
          </span>
          <small>{stopLoss === null ? t('ticket.stopHint') : t('ticket.stopAt', { price: fmt(stopLoss) })}</small>
        </label>
        <label>
          <span>{t('ticket.risk')}</span>
          <span className="stop-input">
            <input inputMode="decimal" value={riskText} onChange={(e) => setRiskText(e.target.value)} aria-label={t('ticket.riskAria')} />
            <span className="unit static">{t('ticket.riskUnit')}</span>
          </span>
          <small>
            {snapshot
              ? t('ticket.equity', { amount: formats().money.format(snapshot.equity), currency: snapshot.depositCurrency })
              : t('ticket.waitingGuardian')}
          </small>
        </label>
        <label>
          <span>{t('ticket.takeProfit')}</span>
          <input
            inputMode="decimal"
            value={takeProfitText}
            onChange={(e) => setTakeProfitText(e.target.value)}
            placeholder={t('ticket.optional')}
            aria-label={t('ticket.takeProfitAria')}
          />
        </label>
      </div>

      <div className="size" aria-live="polite">
        {sizing && sizing.volume > 0 ? (
          <>
            <strong>{t('ticket.lots', { lots: formats().money.format(lotsOf(sizing.volume, symbol?.lotSize ?? 100_000)) })}</strong>
            <span>
              {t('ticket.sizeLine', {
                units: plural('ticket.units', sizing.volume / 100, { count: formats().upTo2.format(sizing.volume / 100) }),
                risk: pct(sizing.riskPercent ?? 0),
              })}
              {sizing.reason === 'capped' ? t('ticket.capped') : ''}
            </span>
          </>
        ) : (
          <span className="muted">{t(sizingHint(sizing?.reason))}</span>
        )}
      </div>

      <VerdictLine state={state} tier2={tier2} snapshotSequence={snapshot?.sequence ?? null} streamStatus={streamStatus} />

      {snapshot && <Budget snapshot={snapshot} />}

      <button
        type="button"
        className={`confirm ${side === 'Buy' ? 'buy' : 'sell'}`}
        disabled={state.status !== 'Ready' || phase.kind === 'deciding' || phase.kind === 'sending'}
        onClick={() => void confirm()}
      >
        {phase.kind === 'deciding'
          ? t('confirm.deciding')
          : phase.kind === 'sending'
            ? t('confirm.sending')
            : t(side === 'Buy' ? 'confirm.buy' : 'confirm.sell', { symbol: symbol?.name ?? '' })}
      </button>

      <PhaseLine phase={phase} onReset={() => setPhase({ kind: 'idle' })} />
    </section>
  )
}

/**
 * Tier 1. Note the ordering of the guards: staleness and clock-anchoring are checked
 * BEFORE any verdict is computed, so a stale snapshot can never render as Ready.
 */
export function useTicketState(
  clock: ServerClock,
  snapshot: DecisionSnapshot | null,
  intent: OrderIntent | null,
  account: number,
): TicketState {
  return useMemo(() => ticketStateOf(clock, snapshot, intent, account), [clock, snapshot, intent, account])
}

export function ticketStateOf(
  clock: ServerClock,
  snapshot: DecisionSnapshot | null,
  intent: OrderIntent | null,
  account: number,
): TicketState {
  if (!snapshot) return { status: 'Checking', reason: 'noSnapshot' }
  // The trader can switch account in cTrader at any moment. Between that and the next
  // snapshot for the new account, what we hold describes the account they just left — and an
  // order would be sent to the new one. Evaluating the old account's limits against the new
  // account's trade is the exact mistake this whole system exists to prevent.
  if (snapshot.account !== account) return { status: 'Checking', reason: 'accountChanged' }
  if (!clock.anchored) return { status: 'Checking', reason: 'clockNotSynced' }
  const nowMs = clock.nowMs()
  if (nowMs >= Date.parse(snapshot.staleAfter)) return { status: 'Checking', reason: 'snapshotStale' }
  if (!intent) return { status: 'Checking', reason: 'noOrder' }
  const verdict = evaluate(snapshot.rules, snapshot, intent, nowMs)
  // "Ready", never "Approved" — only the server approves.
  return verdict.isBlocked ? { status: 'Blocked', verdict } : { status: 'Ready', verdict }
}

function tier1Of(snapshot: DecisionSnapshot, verdict: Verdict): Tier1Report {
  return { snapshotSequence: snapshot.sequence, isBlocked: verdict.isBlocked, violations: verdict.violations.map((v) => v.rule) }
}

/** A key, so the words live in the bundle with every other sentence. */
function sizingHint(reason: string | undefined): Key {
  switch (reason) {
    case 'no-stop':
      return 'sizing.noStop'
    case 'no-equity':
      return 'sizing.noEquity'
    case 'below-minimum':
      return 'sizing.belowMinimum'
    default:
      return 'sizing.waitingPrice'
  }
}

/** The mode word is a translation, not `RuleMode.toLowerCase()`. */
const MODES: Readonly<Record<Violation['mode'], Key>> = {
  Observe: 'mode.observe',
  Warn: 'mode.warn',
  Enforce: 'mode.enforce',
}

const CHECKING: Readonly<Record<CheckingReason, Key>> = {
  noSnapshot: 'checking.noSnapshot',
  accountChanged: 'checking.accountChanged',
  clockNotSynced: 'checking.clockNotSynced',
  snapshotStale: 'checking.snapshotStale',
  noOrder: 'checking.noOrder',
}

function VerdictLine({ state, tier2, snapshotSequence, streamStatus }: {
  state: TicketState
  tier2: { sequence: number; agrees: boolean | null } | null
  snapshotSequence: number | null
  streamStatus: string
}) {
  if (state.status === 'Checking') {
    return (
      <p className="verdict checking">
        {t('checking.label')} <small>({t(streamStatus === 'denied' ? 'checking.denied' : CHECKING[state.reason])})</small>
      </p>
    )
  }
  const confirmed = tier2 && tier2.sequence === snapshotSequence ? tier2.agrees : null
  const warnings = state.verdict.violations.filter((v) => v.mode !== 'Enforce')
  return (
    <div className={`verdict ${state.status === 'Ready' ? 'ready' : 'blocked'}`}>
      {state.status === 'Ready' ? (
        <p>
          {/* One key per whole sentence. German does not take "Ready" and " — risks X" apart
              the way concatenation assumes, and a sentence assembled from fragments is a
              sentence no translator can fix. */}
          {state.verdict.riskPercent === null
            ? t('verdict.ready')
            : t('verdict.readyRisk', { risk: pct(state.verdict.riskPercent) })}
          {confirmed === true ? (
            <small>{t('verdict.confirmed')}</small>
          ) : confirmed === false ? (
            <small className="drift">{t('verdict.disagree')}</small>
          ) : null}
        </p>
      ) : (
        <ul>
          {state.verdict.violations.filter((v) => v.mode === 'Enforce').map((v) => (
            <li key={v.rule}>{ruleMessage(v)}</li>
          ))}
        </ul>
      )}
      {warnings.length > 0 && (
        <ul className="warnings">
          {warnings.map((v) => (
            <li key={v.rule}>
              <span className={`mode ${v.mode.toLowerCase()}`}>{t(MODES[v.mode])}</span> {ruleMessage(v)}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Budget({ snapshot }: { snapshot: DecisionSnapshot }) {
  const locked = snapshot.lockedUntil && Date.parse(snapshot.lockedUntil) > Date.parse(snapshot.serverTime)
  return (
    <div className="budget">
      {locked && <p className="lock">{t('budget.lockedUntil', { when: when(snapshot.lockedUntil!) })}</p>}
      <span>
        {t('budget.dailyLeft')} <strong>{money(snapshot.dailyLossBudgetRemaining)}</strong>
      </span>
      <span>
        {t('budget.openPositions')} <strong>{snapshot.openPositionCount}</strong> · {t('budget.tradesToday')}{' '}
        <strong>{snapshot.tradesTakenToday}</strong>
      </span>
    </div>
  )
}

function PhaseLine({ phase, onReset }: { phase: Phase; onReset: () => void }) {
  switch (phase.kind) {
    case 'filled':
      return (
        <p className="phase good">
          {t('phase.filled')} <small>{phase.label}</small>{' '}
          <button type="button" className="link" onClick={onReset}>
            {t('phase.another')}
          </button>
        </p>
      )
    case 'refused':
      return (
        <div className="phase bad">
          <p>{t('phase.refused')}</p>
          <ul>
            {phase.violations.map((v) => (
              <li key={v.rule}>{ruleMessage(v)}</li>
            ))}
          </ul>
          <button type="button" className="link" onClick={onReset}>
            {t('phase.ok')}
          </button>
        </div>
      )
    case 'failed':
      return (
        <div className="phase bad">
          <ProblemLine problem={phase.problem} className="" />
          <button type="button" className="link" onClick={onReset}>
            {t('phase.ok')}
          </button>
        </div>
      )
    default:
      return null
  }
}
